import React, { createContext, useContext, useState, useEffect } from 'react';
import { io } from 'socket.io-client';

const SOCKET_SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001'; // Updated to match the server port in .env file

// Create the context with a default empty value
const SocketContext = createContext({
  socket: null,
  connected: false,
  socketId: null,
  processingSteps: {},
  resetProcessingSteps: () => { }
});

// Export the hook
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    console.error('useSocket must be used within a SocketProvider');
    // Return default values instead of throwing an error
    return {
      socket: null,
      connected: false,
      socketId: null,
      processingSteps: {},
      resetProcessingSteps: () => { }
    };
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const [processingSteps, setProcessingSteps] = useState({});
  const [verboseDebugging, setVerboseDebugging] = useState(true);

  useEffect(() => {

    try {
      // Initialize socket connection with improved configuration
      const socketInstance = io(SOCKET_SERVER_URL, {
        transports: ['websocket'], // Use only WebSockets without polling fallback
        reconnection: true,        // Enable reconnection
        reconnectionAttempts: 5,   // Number of reconnection attempts
        reconnectionDelay: 1000,   // Starting delay between reconnections (1s)
        reconnectionDelayMax: 5000, // Maximum delay between reconnections (5s)
        timeout: 20000,            // Connection timeout
        autoConnect: true          // Auto connect on creation
      });



      socketInstance.on('connect', () => {
        setConnected(true);
        setSocketId(socketInstance.id);
      });

      socketInstance.on('disconnect', () => {
        setConnected(false);
        setSocketId(null);
      });

      socketInstance.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        setConnected(false);
      });

      socketInstance.on('processing_update', (updateData) => {
        setProcessingSteps(prevSteps => {
          // If it's an error, store it in the error key
          if (updateData.status === 'error') {
            return {
              ...prevSteps,
              error: {
                status: 'error',
                message: updateData.message,
                timestamp: new Date().getTime()
              }
            };
          }

          // For normal updates
          return {
            ...prevSteps,
            [updateData.step]: {
              status: updateData.status,
              message: updateData.message,
              progress: updateData.progress,
              data: updateData.data || {},
              timestamp: new Date().getTime()
            }
          };
        });
      });

      // Add handler for initial processing steps
      socketInstance.on('init_processing_steps', (steps) => {

        if (Object.keys(steps).length > 0) {
          // Convert the steps object to match our format
          const formattedSteps = {};

          for (const [stepId, stepData] of Object.entries(steps)) {
            formattedSteps[stepId] = {
              status: stepData.status,
              message: stepData.message,
              progress: stepData.progress,
              data: stepData.data || {},
              timestamp: stepData.timestamp || new Date().getTime()
            };
          }

          // Only update if we have steps
          setProcessingSteps(formattedSteps);
        } else {
          // Reset if no steps
          setProcessingSteps({});
        }
      });

      // Store socket instance
      setSocket(socketInstance);

      // Clean up on unmount
      return () => {
        if (socketInstance) {
          socketInstance.disconnect();
        }
      };
    } catch (error) {
      console.error('Error initializing socket:', error);
    }
  }, []);

  // Reset processing steps
  const resetProcessingSteps = () => {
    setProcessingSteps({});
  };

  const value = {
    socket,
    connected,
    socketId,
    processingSteps,
    resetProcessingSteps
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

// Export as default to avoid import confusion
export default SocketContext;
